import { useCallback, useEffect, useRef, useState } from 'react';

const sleep = (ms) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });

const randomInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

const getPathFromRootToNode = (graphState, nodeId) => {
  const nodesById = graphState?.nodesById || {};
  const rootId = graphState?.rootNodeId || null;
  if (!nodeId || !rootId || !nodesById[nodeId]) return null;

  const visited = new Set();
  const path = [];
  let currentId = nodeId;

  while (currentId) {
    if (visited.has(currentId)) return null;
    visited.add(currentId);
    path.push(currentId);
    if (currentId === rootId) break;
    currentId = nodesById[currentId]?.parentId || null;
  }

  if (path[path.length - 1] !== rootId) return null;
  return path.reverse();
};

const buildExecutionPlan = (graphState, targetNodeId = null) => {
  const nodesById = graphState?.nodesById || {};
  const rootId = graphState?.rootNodeId || null;
  if (!rootId || !nodesById[rootId]) return [];

  let allowedIds = null;
  if (targetNodeId) {
    const path = getPathFromRootToNode(graphState, targetNodeId);
    if (!path) return [];
    allowedIds = new Set(path);
  }

  const queue = [rootId];
  const visited = new Set();
  const executionOrder = [];

  while (queue.length > 0) {
    const currentId = queue.shift();
    if (!currentId || visited.has(currentId)) continue;
    if (allowedIds && !allowedIds.has(currentId)) continue;
    visited.add(currentId);
    executionOrder.push(currentId);

    if (targetNodeId && currentId === targetNodeId) continue;

    const children = nodesById[currentId]?.childIds || [];
    children.forEach((childId) => {
      if (!visited.has(childId)) queue.push(childId);
    });
  }

  return executionOrder;
};

/**
 * Executes simulated graph traversal and updates node statuses/counts.
 */
export const useGraphExecution = ({
  graphState,
  graphStateRef,
  applyGraphAction,
  showFeedback,
  graphActions,
  onPrepareExecution,
}) => {
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionProgress, setExecutionProgress] = useState(0);
  const [executionTargetNodeId, setExecutionTargetNodeId] = useState(null);
  const [executionMode, setExecutionMode] = useState('full');
  const [executionLogs, setExecutionLogs] = useState([]);
  const [showExecutionLog, setShowExecutionLog] = useState(false);
  const [lastExecutionAt, setLastExecutionAt] = useState(graphState.metadata?.lastExecutedAt || null);
  const executionRunIdRef = useRef(0);

  useEffect(() => {
    setLastExecutionAt(graphState.metadata?.lastExecutedAt || null);
  }, [graphState.metadata]);

  const appendExecutionLog = useCallback((message) => {
    const timestamp = new Date().toLocaleTimeString();
    setExecutionLogs((previous) => [`[${timestamp}] ${message}`, ...previous].slice(0, 24));
  }, []);

  const runExecutionSimulation = useCallback(
    async (targetNodeId = null, mode = 'full') => {
      if (isExecuting) return;

      const currentGraph = graphStateRef.current;
      const nodeIds = Object.keys(currentGraph.nodesById || {});
      if (nodeIds.length === 0) {
        showFeedback('Please create or load a graph before executing.', 'warning');
        return;
      }

      const executionPlan = buildExecutionPlan(currentGraph, targetNodeId);
      if (executionPlan.length === 0) {
        showFeedback(
          'Could not build an execution plan for the selected node. Check parent-child links and breakpoint.',
          'error',
        );
        return;
      }

      const runId = executionRunIdRef.current + 1;
      executionRunIdRef.current = runId;

      setIsExecuting(true);
      setExecutionProgress(0);
      setExecutionTargetNodeId(targetNodeId || null);
      setExecutionMode(mode);
      setExecutionLogs([]);
      onPrepareExecution?.();

      if (mode === 'breakpoint' && targetNodeId) {
        appendExecutionLog(`Started execution to breakpoint node ${targetNodeId}.`);
      } else if (targetNodeId) {
        appendExecutionLog(`Started partial execution to node ${targetNodeId}.`);
      } else {
        appendExecutionLog('Started full graph execution.');
      }

      const resetNodesById = {};
      nodeIds.forEach((nodeId) => {
        const node = currentGraph.nodesById[nodeId];
        resetNodesById[nodeId] = {
          ...node,
          status: 'pending',
          patientCount: null,
          isExecuted: false,
        };
      });

      applyGraphAction(
        {
          type: graphActions.SET_GRAPH,
          payload: {
            ...currentGraph,
            nodesById: resetNodesById,
            lastError: null,
          },
        },
        { recordHistory: true },
      );

      const countsByNode = {};

      try {
        for (let index = 0; index < executionPlan.length; index += 1) {
          if (executionRunIdRef.current !== runId) return;

          const nodeId = executionPlan[index];
          const node = graphStateRef.current.nodesById?.[nodeId];
          if (!node) continue;

          applyGraphAction(
            {
              type: graphActions.UPDATE_NODE,
              payload: {
                nodeId,
                patch: {
                  status: 'executing',
                  patientCount: null,
                  isExecuted: false,
                },
              },
            },
            { recordHistory: false },
          );

          await sleep(randomInt(500, 1000));
          if (executionRunIdRef.current !== runId) return;

          const currentNode = graphStateRef.current.nodesById?.[nodeId] || node;
          let nextStatus = 'completed';
          let nextPatientCount = null;
          let nextIsExecuted = false;

          if (index === 0) {
            nextPatientCount = randomInt(500, 5000);
            nextIsExecuted = true;
          } else {
            const parentId = currentNode.parentId;
            const parentCount = Number.isFinite(countsByNode[parentId]) ? countsByNode[parentId] : null;

            if (!Number.isFinite(parentCount) || parentCount < 10 || Math.random() < 0.04) {
              nextStatus = 'error';
            } else {
              const criteriaCount =
                (currentNode.includeConditions?.length || 0) + (currentNode.excludeConditions?.length || 0);
              const minimumFactor = 0.4;
              const maximumFactor = Math.max(0.45, 0.9 - Math.min(0.35, criteriaCount * 0.05));
              const factor = minimumFactor + Math.random() * Math.max(0.001, maximumFactor - minimumFactor);
              nextPatientCount = Math.max(10, Math.floor(parentCount * factor));
              nextIsExecuted = true;
            }
          }

          if (nextStatus === 'error') {
            countsByNode[nodeId] = null;
            appendExecutionLog(`${currentNode.name}: execution failed.`);
          } else {
            countsByNode[nodeId] = nextPatientCount;
            appendExecutionLog(`${currentNode.name}: ${nextPatientCount.toLocaleString()} patients.`);
          }

          applyGraphAction(
            {
              type: graphActions.UPDATE_NODE,
              payload: {
                nodeId,
                patch: {
                  status: nextStatus,
                  patientCount: nextPatientCount,
                  isExecuted: nextIsExecuted,
                },
              },
            },
            { recordHistory: false },
          );

          setExecutionProgress(Math.round(((index + 1) / executionPlan.length) * 100));
        }

        if (mode === 'breakpoint' && targetNodeId) {
          appendExecutionLog(`Breakpoint reached at node ${targetNodeId}.`);
        }
        appendExecutionLog('Execution completed.');

        const finishedAtIso = new Date().toISOString();
        setLastExecutionAt(finishedAtIso);
        applyGraphAction(
          {
            type: graphActions.SET_GRAPH,
            payload: {
              ...graphStateRef.current,
              metadata: {
                ...graphStateRef.current.metadata,
                lastExecutedAt: finishedAtIso,
              },
            },
          },
          { recordHistory: false },
        );

        showFeedback(
          mode === 'breakpoint' && targetNodeId
            ? `Execution completed at breakpoint node ${targetNodeId}.`
            : targetNodeId
              ? `Execution completed up to node ${targetNodeId}.`
              : 'Graph execution completed.',
          'success',
        );
      } finally {
        if (executionRunIdRef.current === runId) {
          setIsExecuting(false);
          setExecutionTargetNodeId(null);
          setExecutionMode('full');
        }
      }
    },
    [
      appendExecutionLog,
      applyGraphAction,
      graphActions.SET_GRAPH,
      graphActions.UPDATE_NODE,
      graphStateRef,
      isExecuting,
      onPrepareExecution,
      showFeedback,
    ],
  );

  const executeGraph = useCallback(() => {
    runExecutionSimulation(null, 'full');
  }, [runExecutionSimulation]);

  const executeToHere = useCallback(
    (nodeId) => {
      runExecutionSimulation(nodeId, 'node');
    },
    [runExecutionSimulation],
  );

  const executeToBreakpoint = useCallback(() => {
    const targetNodeId = graphStateRef.current.metadata?.breakpointNodeId || null;
    if (!targetNodeId) {
      showFeedback('Set a breakpoint on a node first.', 'warning');
      return;
    }
    runExecutionSimulation(targetNodeId, 'breakpoint');
  }, [graphStateRef, runExecutionSimulation, showFeedback]);

  return {
    isExecuting,
    executionProgress,
    executionTargetNodeId,
    executionMode,
    executionLogs,
    showExecutionLog,
    setShowExecutionLog,
    lastExecutionAt,
    executeGraph,
    executeToHere,
    executeToBreakpoint,
  };
};

export default useGraphExecution;
